import isEmail from 'validator/lib/isEmail';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, includes } from 'lodash-es';
import { Alert, Checkbox, Container, Paper, PrimaryButton, TertiaryButton, Typography as T } from '@dev/ee-components';
import { ConsentTracking, ContactStatus } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { push as Push } from 'redux-first-history';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';

import { AddSingleContact, getIsCreatingContactsStore } from '@/store/contacts/actions';
import { ListsLoadStart } from '@/store/lists/actions';
import { ContactsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { debounce } from '@/services/tools/debounce';
import useMobile from '@/services/hooks/useMobile';
import {
    ScreenWrapper,
    ContactFormSection,
    AddContactForm,
    FooterContainer,
    StyledMobile,
} from './components/StyledComponents';
import { PostContactsParamsProps, ALL_CONTACTS_LIST, ALL_CONTACTS_LIST_NAME } from './AddInterface';
import { Navigation } from './components/Navigation';
import { getContactDetailsInputConfig } from './config/ContactDetailsInputsConfig';
import { ContactDetailsInput } from './components/ContactDetailsInput';
import { ListDetails } from './components/ListDetails';
import { SelectTrackingRadio } from './components/SelectTrackingRadio';

const ContactsAdd = (): JSX.Element => {
    const match = useRouteMatch();
    const { segmentname, listname } = match.params as any;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useMobile();
    const isCreatingContacts = useSelector(getIsCreatingContactsStore);

    const getSelectedListName = (): string => (listname ? safelyDecodeBase64Url(listname) : ALL_CONTACTS_LIST);

    const defaultPostContactsParams: PostContactsParamsProps = {
        body: [],
        status: ContactStatus.Active,
        consentTracking: ConsentTracking.Unknown,
        listName: getSelectedListName(),
    };
    const [communicationApproval, setCommunicationApproval] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [recieveComunicationError, setRecieveComunicationError] = useState(false);
    const [postContactsParams, setPostContactsParams] = useState<PostContactsParamsProps>(defaultPostContactsParams);
    const formRef = useRef();
    const anotherContactRef: any = React.createRef<HTMLInputElement>();
    const contactDetailsInputsConfig = getContactDetailsInputConfig(postContactsParams);

    useEffect(() => {
        dispatch(ListsLoadStart({ limit: 0, offset: 0, modifyQueryString: false }));
    }, []);

    const handleSetAddContactParam = (param: string, target: string): void => {
        if (param === 'email') {
            debounce(() => {
                const isValidEmail = isEmail(target);
                setEmailError(!isValidEmail);
            }, 500);
        }
        setAddContactParam(param, target);
    };

    const renderContactPermissions = (): JSX.Element => (
        <div>
            <Checkbox
                name="addContactsTrackingPermission"
                id="contacts-tracking-permission-checkbox"
                onChange={(e, obj): void => {
                    setCommunicationApproval(obj);
                    setRecieveComunicationError(!obj);
                }}
                error={recieveComunicationError}
                errorMsg={t('descconsentsubquickadd_errormsg')}
                checked={communicationApproval}
                labelPlacement="end"
                label={t('descconsentsubquickadd')}
            />
            <div style={{ marginTop: '34px' }} className="permissionRadioGroup">
                <T variant="h4">{t('trackingconsentquickadd')}</T>
                <SelectTrackingRadio setAddContactParam={setAddContactParam} postContactsParams={postContactsParams} />
            </div>
        </div>
    );

    const setAddContactParam = (prop, value): void => {
        const newContact = cloneDeep(postContactsParams);
        if (includes(['firstname', 'lastname', 'email'], prop)) {
            if (newContact.body.length) {
                newContact.body[0][prop] = value;
            } else {
                newContact.body[0] = {};
                newContact.body[0][prop] = value;
            }
        } else {
            newContact[prop] = value;
        }
        setPostContactsParams(newContact);
    };

    const resetForm = (): void => {
        const resetContact = {
            body: [],
            status: ContactStatus.Active,
            consentTracking: ConsentTracking.Unknown,
            listName: listname ? safelyDecodeBase64Url(listname) : ALL_CONTACTS_LIST,
        };
        setCommunicationApproval(false);
        setPostContactsParams(resetContact);
    };

    const handleAddContact = (): void => {
        const listName = getSelectedListName();
        let redirectPath;
        if (!anotherContactRef.current.checked) {
            redirectPath = listname
                ? ContactsPaths.SingleList.replace(
                      pathsParams.listName,
                      safelyEncodeBase64Url(postContactsParams?.listName || listName)
                  )
                : ContactsPaths.ContactsOverview;
            if (segmentname && postContactsParams.listName === ALL_CONTACTS_LIST) {
                redirectPath = ContactsPaths.SingleSegment.replace(
                    pathsParams.segmentName,
                    safelyEncodeBase64Url(t(ALL_CONTACTS_LIST_NAME))
                );
            }
        }
        dispatch(AddSingleContact(postContactsParams, redirectPath));
        anotherContactRef.current.checked && resetForm();
    };

    const validateTheFormAndAdd = (): void => {
        const hasValidEmail = isEmail(postContactsParams?.body?.[0]?.email || '');
        if (!hasValidEmail || !communicationApproval) {
            setEmailError(!hasValidEmail);
            setRecieveComunicationError(!communicationApproval);
            return;
        }
        return handleAddContact();
    };

    return (
        <Container>
            <ScreenWrapper>
                <Navigation segmentName={segmentname as string} listName={listname as string} />
                <StyledMobile isMobile={isMobile}>
                    <Paper>
                        <div className="paperStyles">
                            <Alert
                                severity="info"
                                body={
                                    <T variant="body1" bodyVariant="onPaper">
                                        {t('youcaneasilyuploaddesc')}
                                        <TertiaryButton
                                            onClick={() => dispatch(Push(ContactsPaths.ContactsUpload))}
                                            text={t('addagroupofcontacts')}
                                        />
                                    </T>
                                }
                            />
                            <AddContactForm id="add-single-contact-form" ref={formRef} isMobile={isMobile}>
                                <ContactFormSection>
                                    <h2>
                                        <span className="numberItem">1</span>
                                        {t('addcontactdetails')}
                                    </h2>
                                    <Grid container alignContent="flex-start">
                                        {contactDetailsInputsConfig.map((input) => (
                                            <ContactDetailsInput
                                                key={input.name}
                                                name={input.name}
                                                setAddContactParam={handleSetAddContactParam}
                                                value={input.value}
                                                emailError={input.errorMessage && emailError}
                                                errorMessage={input.errorMessage && t(input.errorMessage)}
                                            />
                                        ))}
                                    </Grid>
                                </ContactFormSection>
                                <ContactFormSection>
                                    <h2>
                                        <span className="numberItem">2</span>
                                        {t('choosecontactlist')}
                                    </h2>
                                    <ListDetails
                                        postContactsParams={postContactsParams}
                                        listName={listname}
                                        segmentName={segmentname}
                                        setAddContactParam={setAddContactParam}
                                    />
                                </ContactFormSection>
                                <ContactFormSection className="permissionsSection">
                                    <h2>
                                        <span className="numberItem">3</span>
                                        {t('setcontactpermissions')}
                                    </h2>
                                    {renderContactPermissions()}
                                </ContactFormSection>
                            </AddContactForm>
                        </div>
                        <FooterContainer isMobile={isMobile}>
                            <Checkbox
                                id="add-another-contact-checkbox"
                                name="addAnother"
                                inputRef={anotherContactRef}
                                labelPlacement="end"
                                label={t('addanother')}
                            />
                            <PrimaryButton
                                id="add-contact-button"
                                text={t('add')}
                                disabled={isCreatingContacts}
                                onClick={validateTheFormAndAdd}
                            />
                        </FooterContainer>
                    </Paper>
                </StyledMobile>
            </ScreenWrapper>
        </Container>
    );
};

export default ContactsAdd;
